import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, of as observableOf, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { FIELD_TYPES } from '../../../../../shared/fields/field-types';
import { STANDARD_FIELDS } from '../../../../../shared/fields/standard-fields';
import * as fromRoot from '../../../core/store';
import * as assetActions from '../../../core/store/assets/asset.actions';
import { Field, FieldWithDepartmentField } from '../../../core/store/fields/field.model';
import { getSelectedGroupIds, getSelectedGroups, getSelectedSubGroupIds } from '../../../core/store/groups/group-utils';
import * as layoutActions from '../../../core/store/layout/layout.actions';
import { Asset, Department, DepartmentField, Franchise, Group, ProjectCurrency } from '../../../core/store/models';
import { rootSelectors } from '../../../core/store/selectors';
import { ActionStatus } from '../../../core/store/types';
import { PDropdownOption } from '../../../shared/interfaces/p-dropdown-options';
import * as subGroupActions from "./../../../core/store/sub-groups/sub-group.actions";

export interface InlineEditModalData {
    allGroups$: Observable<Group[]>;
    asset: Asset;
    departments: Department[];
    divisionId: number;
    divisionGroups$: Observable<Group[]>;
    notDivisionGroups$: Observable<Group[]>;
    event: MouseEvent;
    field: Field;
    franchise?: Franchise;
    franchiseGroups$: Observable<Group[]>;
    notFranchiseGroups$: Observable<Group[]>;
    isEventTable: boolean;
    isFranchiseTable: boolean;
    isTransactionMatchReviewTable: boolean;
    isTransactionMatchTable: boolean;
    options$?: Observable<PDropdownOption[]>;
    isDivisionTable: boolean;
    isFranchiseGroupTable: boolean;
    isDivisionGroupTable: boolean;
    isFranchiseSubGroupTable: boolean;
    isDivisionSubGroupTable: boolean;
}

@Component({
    templateUrl: './inline-edit-modal.component.html',
    styleUrls: ['./inline-edit-modal.component.scss'],
    standalone: false
})
export class InlineEditModalComponent implements OnInit, OnDestroy {
    allGroups$: Observable<Group[]>;
    asset: Asset;
    assetField: FieldWithDepartmentField = null;
    departments: Department[];
    divisionId: number;
    divisionGroups$: Observable<Group[]>;
    notDivisionGroups$: Observable<Group[]>;
    editError: string;
    editForm: UntypedFormGroup;
    event: MouseEvent;
    field: Field;
    franchise: Franchise;
    franchiseGroups$: Observable<Group[]>;
    notFranchiseGroups$: Observable<Group[]>;
    isEventTable: boolean;
    isFranchiseTable: boolean;
    isDivisionTable: boolean;
    isFranchiseGroupTable: boolean;
    isDivisionGroupTable: boolean;
    isFranchiseSubGroupTable: boolean;
    isDivisionSubGroupTable: boolean;
    isTransactionMatchReviewTable: boolean;
    isTransactionMatchTable: boolean;
    options$: Observable<PDropdownOption[]> = observableOf([]);
    selectedGroups: string[];
    selectedOptions: string[];
    selectedSubGroups: string[];
    projectCurrencies: ProjectCurrency[];
    basePC: ProjectCurrency;
    originPC: ProjectCurrency;
    USPC: ProjectCurrency;
    franchiseTable: boolean;
    divisionTable: boolean;
    subs: Subscription;
    constructor(@Inject(MAT_DIALOG_DATA) private data: InlineEditModalData, public dialogRef: MatDialogRef<InlineEditModalComponent>, private fb: UntypedFormBuilder, private store: Store<rootSelectors.State>) { }

    ngOnInit() {
        this.allGroups$ = this.data.allGroups$;
        this.asset = this.data.asset;
        this.departments = this.data.departments;
        this.divisionId = this.data.divisionId;
        this.event = this.data.event;
        this.field = this.data.field;
        this.isFranchiseTable = this.data.isFranchiseTable;
        this.isDivisionTable = this.data.isDivisionTable;
        this.isFranchiseGroupTable = this.data.isFranchiseGroupTable;
        this.isDivisionGroupTable = this.data.isDivisionGroupTable;
        this.isFranchiseSubGroupTable = this.data.isFranchiseSubGroupTable;
        this.isDivisionSubGroupTable = this.data.isDivisionSubGroupTable;
        this.isEventTable = this.data.isEventTable;
        this.isTransactionMatchReviewTable = this.data.isTransactionMatchReviewTable;
        this.isTransactionMatchTable = this.data.isTransactionMatchTable;
        this.isEventTable = this.data.isEventTable;
        this.options$ = this.data.options$;
        this.divisionGroups$ = this.data.divisionGroups$;
        this.notDivisionGroups$ = this.data.notDivisionGroups$;
        this.franchiseGroups$ = this.data.franchiseGroups$;
        this.notFranchiseGroups$ = this.data.notFranchiseGroups$;
        this.franchise = this.data.franchise;
        this.divisionTable = this.isDivisionTable || this.isDivisionGroupTable || this.isEventTable || this.isDivisionSubGroupTable;
        this.franchiseTable = this.isFranchiseTable || this.isFranchiseGroupTable || this.isFranchiseSubGroupTable;

        this.store.select(fromRoot.getProjectCurrencies).pipe(take(1)).subscribe(res => {
            this.projectCurrencies = res.filter(c => c.project_id === this.asset.project_id);
        });

        if (window.outerWidth - 300 < this.event.clientX) {
            this.dialogRef.updatePosition({ top: this.event.clientY + 'px', left: this.event.clientX - 250 + 'px' });
        } else {
            this.dialogRef.updatePosition({ top: this.event.clientY + 'px', left: this.event.clientX + 'px' });
        }
        this.store.dispatch(new assetActions.SelectInlineEditAction(this.asset.id));
        if (this.field == undefined) {
            this.editError = 'This field can not be edited inline.';

        } else {
            const dept = this.departments.find(d => d.id === this.asset.department_id);
            const departmentField: DepartmentField = dept.departmentFields.find(df => df.field_id === this.field.id);

            const uneditableFields = [STANDARD_FIELDS.photos.canonical_name, STANDARD_FIELDS.qty_on_hand.canonical_name, STANDARD_FIELDS.sync_id.canonical_name,
            STANDARD_FIELDS.created_at.canonical_name, STANDARD_FIELDS.updated_at.canonical_name, STANDARD_FIELDS.division.canonical_name, STANDARD_FIELDS.franchise.canonical_name,
            STANDARD_FIELDS.project.canonical_name, STANDARD_FIELDS.department.canonical_name, STANDARD_FIELDS.location.canonical_name, STANDARD_FIELDS.subLocation.canonical_name,
            STANDARD_FIELDS.warehouse.canonical_name, STANDARD_FIELDS.warehouseLocation.canonical_name, STANDARD_FIELDS.warehouseSubLocation.canonical_name,
            STANDARD_FIELDS.storageBox.canonical_name, STANDARD_FIELDS.sub_code.canonical_name, 'cost_other', 'local_cost', STANDARD_FIELDS.assetEvents.canonical_name];

            const uneditableFieldsForDivision = [STANDARD_FIELDS.character.canonical_name, STANDARD_FIELDS.origin_currency.canonical_name, STANDARD_FIELDS.set.canonical_name,
            STANDARD_FIELDS.sub_department.canonical_name];

            if (uneditableFields.includes(this.field.canonical_name) || (!departmentField && this.field.canonical_name !== STANDARD_FIELDS.group_ids.canonical_name) || (this.isFranchiseTable && this.asset.division_id) || (this.asset.division_id && uneditableFieldsForDivision.includes(this.field.canonical_name))) {
                this.assetField = null;
                if (this.isFranchiseTable && this.asset.division_id) {
                    this.editError = 'Division Assets cannot be edited in a Franchise.';
                } else if (uneditableFields.includes(this.field.canonical_name)) {
                    this.editError = 'This field can not be edited inline.';
                } else if (this.asset.division_id && uneditableFieldsForDivision.includes(this.field.canonical_name)) {
                    this.editError = 'This field can not be edited for division assets.';
                } else {
                    this.editError = 'This field does not exist for this department.';
                }
            } else if (this.field.type === 'MULTI_PICKLIST' && this.field.canonical_name !== STANDARD_FIELDS.group_ids.canonical_name) {
                this.selectedOptions = [];
                const selectedField = this.asset[this.field.canonical_name];
                this.assetField = { departmentField: null, field: this.field };
                this.editForm = this.fb.group({});
                if (selectedField && selectedField.length) {
                    this.selectedOptions = this.getSelectedOptions(this.asset, this.options$, this.field.canonical_name)
                }
            } else if (this.field.canonical_name === STANDARD_FIELDS.group_ids.canonical_name) {
                if (this.isEventTable && (!this.asset.division_id || this.asset.division_id !== this.divisionId)) {
                    this.editError = 'This field can not be edited for assets not in this division';
                }
                else {
                    this.selectedGroups = [];
                    this.selectedSubGroups = [];
                    this.assetField = { departmentField: null, field: this.field };
                    this.editForm = this.fb.group({});

                    if (this.asset.group_ids && this.asset.group_ids.length) {
                        this.selectedGroups = getSelectedGroups(this.franchiseGroups$, this.divisionGroups$, this.divisionTable, this.asset, this.franchiseTable, this.allGroups$);
                    }
                    if (this.asset.sub_group_ids && this.asset.sub_group_ids.length) {
                        this.store.dispatch(new subGroupActions.GetAssetSubGroupsNamesAction({ subgroupIds: this.asset.sub_group_ids }));
                        this.subs = this.store.select(fromRoot.getSubGroupNames).subscribe((subGroupNames) => {
                            this.selectedSubGroups = Array.from(Object.values(subGroupNames));
                        });
                    }
                }
            } else if (this.field.canonical_name === STANDARD_FIELDS.shelf.canonical_name && this.asset.storage_box_id) {
                this.editError = 'This field can not be edited inline.';
            } else {
                if (this.field.canonical_name === STANDARD_FIELDS.cost.canonical_name && (this.isFranchiseTable || this.isTransactionMatchTable || this.isTransactionMatchReviewTable)) {
                    this.field = new Field({ ...this.field });
                }

                this.assetField = { departmentField, field: this.field };

                let value = this.asset[this.field.canonical_name];
                if (this.field.type === 'DATE') {
                    value = new Date(this.asset[this.field.canonical_name]);
                } else if (this.field.canonical_name === STANDARD_FIELDS.cost.canonical_name) {
                    if (this.projectCurrencies && this.projectCurrencies.length > 0) {
                        this.USPC = this.projectCurrencies.find(c => c.currency_id === 1);
                        this.originPC = this.projectCurrencies.find(c => c.id == this.asset.origin_currency);
                        if (this.asset.origin_currency !== this.USPC.id) {
                            value = this.asset.allCosts[this.asset.origin_currency];
                        }
                        value = Number((parseFloat(value) * parseFloat(this.USPC.rate)) / parseFloat(this.originPC.rate)).toFixed(2);
                    } else {
                        value = Number(value).toFixed(2);
                    }
                }
                const initial = {
                    value,
                    disabled: false
                };
                const validators = this.field.getValidators(departmentField.required);

                const formObj = {};
                formObj[this.field.canonical_name] = [initial, validators];
                this.editForm = this.fb.group(formObj);
            }
        }
    }

    getSelectedOptions(asset: Asset, allOptions$: Observable<PDropdownOption[]>, fieldName) {
        const selectedOptions = [];
        allOptions$.pipe(take(1)).subscribe(options => {
            asset[fieldName].forEach(option => {
                const selectedOption = options.find(g => g.value === option);
                if (selectedOption) {
                    selectedOptions.push(selectedOption.label);
                }
            });
        });
        return selectedOptions;
    }

    saveEditedAsset() {
        let newAsset: Asset;
        if (this.field.canonical_name === STANDARD_FIELDS.group_ids.canonical_name) {
            newAsset = new Asset({ ...this.asset });
            newAsset.group_ids = getSelectedGroupIds(this.divisionGroups$, this.notDivisionGroups$, this.franchiseGroups$, this.notFranchiseGroups$, this.divisionTable, this.franchiseTable, this.selectedGroups, this.allGroups$);
            newAsset.sub_group_ids = getSelectedSubGroupIds(this.divisionGroups$, this.notDivisionGroups$, this.franchiseGroups$, this.notFranchiseGroups$, this.divisionTable, this.franchiseTable, this.selectedSubGroups, this.allGroups$);
        } else if (this.field.type === FIELD_TYPES.MULTI_PICKLIST.type) {
            newAsset = new Asset({ ...this.asset });
            newAsset[this.field.canonical_name] = this.getSelectedOptionIdsFromObs(this.options$, this.selectedOptions)
        } else {
            if (this.editForm.invalid) {
                return;
            }
            if (this.field.type === FIELD_TYPES.LONG_TEXT_AREA.type || this.field.type === FIELD_TYPES.SHORT_TEXT_AREA.type || this.field.type === FIELD_TYPES.HUGE_TEXT_AREA.type) {
                const value = this.editForm.value[this.field.canonical_name].trim();
                this.editForm.patchValue({ [this.field.canonical_name]: value })
            }
            if (this.field.canonical_name === STANDARD_FIELDS.cost.canonical_name) {
                if (this.projectCurrencies && this.projectCurrencies.length > 0) {
                    this.USPC = this.projectCurrencies.find(c => c.currency_id === 1);
                    this.originPC = this.projectCurrencies.find(c => c.id == this.asset.origin_currency);

                    const value = Number((parseFloat(this.editForm.value.cost) * parseFloat(this.originPC.rate)) / parseFloat(this.USPC.rate)).toFixed(2);
                    newAsset = new Asset({ ...this.asset, ...this.editForm.getRawValue(), ...{ cost: value.toString() } });
                } else {
                    newAsset = new Asset({ ...this.asset, ...this.editForm.getRawValue() });
                }
            } else {
                newAsset = new Asset({ ...this.asset, ...this.editForm.getRawValue() });
            }
            if (this.field.canonical_name !== STANDARD_FIELDS.cost.canonical_name) {
                delete newAsset.cost; // If cost is not being updated, we don't want to pass the potential asset's calculated cost in another currency
            }
        }

        this.store.dispatch(new assetActions.UpdateAction({ assets: [newAsset], forceRecalculate: true }));

        this.store.select(fromRoot.getAssetLoading).pipe(
            filter(loading => loading !== ActionStatus.Loading),
            take(1))
            .subscribe((loading) => {
                if (loading === ActionStatus.Complete) {
                    const message = 'Asset has been updated.';
                    this.store.dispatch(new layoutActions.InfoAction({ message }));
                    this.dialogRef.close(true);
                }
            });
    }

    getSelectedOptionIdsFromObs(options$: Observable<any[]>, selectedOptions: string[]) {
        const selectedGroupIds = [];
        options$.pipe(take(1)).subscribe(options => {
            selectedOptions.forEach(selectedGroup => {
                const foundSelectedGroup = options.find(g => g.label === selectedGroup);
                if (foundSelectedGroup) {
                    selectedGroupIds.push(foundSelectedGroup.value);
                }
            });
        });
        return selectedGroupIds;
    }

    ngOnDestroy() {
        if (this.subs)
            this.subs.unsubscribe();
    }
}
